import type { LookupConfigKind, StoreFormValues } from 'src/features/store/types/store.types';
import { formatInstant, zoneAbbreviation } from 'src/features/timezone';
import {
  DataEnumerationsDiscountCodeProvider,
  DataEnumerationsLookupMode,
  DataEnumerationsLoyaltyProgramProvider,
  DataEnumerationsPosSystem,
} from 'src/types/api/generated/api';

export const MODE_OPTIONS = [
  {
    value: DataEnumerationsLookupMode.Disabled,
    label: 'Off',
    loyaltyLabel: 'No loyalty program',
    helperText: 'The feature is unavailable and never blocks checkout.',
  },
  {
    value: DataEnumerationsLookupMode.RequiredPreOrder,
    label: 'Required before shopping starts',
    helperText: 'Lookup must succeed before the patron can begin shopping.',
  },
  {
    value: DataEnumerationsLookupMode.OptionalPreOrderRequiredAtCheckout,
    label: 'Offered before shopping, required at checkout',
    helperText: 'Lookup is offered before shopping and must succeed before checkout.',
  },
  {
    value: DataEnumerationsLookupMode.RequiredAtCheckout,
    label: 'Required at checkout',
    helperText: 'Lookup must succeed before the order can move to Review.',
  },
  {
    value: DataEnumerationsLookupMode.OptionalPostAttract,
    label: 'Optional button during shopping',
    helperText: 'Lookup is optionally available after the attract screen.',
  },
];

export const lookupModeLabel = (mode: DataEnumerationsLookupMode): string =>
  MODE_OPTIONS.find((option) => option.value === mode)?.label ?? mode;

const REAL_POS_SYSTEMS = Object.values(DataEnumerationsPosSystem).filter(
  (posSystem) => posSystem !== DataEnumerationsPosSystem.None,
);

const POS_SYSTEM_LABELS: Partial<Record<DataEnumerationsPosSystem, string>> = {
  [DataEnumerationsPosSystem.NSPOS]: 'Netsuite',
  [DataEnumerationsPosSystem.TeamworkCommerce]: 'Teamwork Commerce',
};

export const posSystemLabel = (posSystem: DataEnumerationsPosSystem): string =>
  POS_SYSTEM_LABELS[posSystem] ?? posSystem;

export const allowedPosSystems = (
  kind: LookupConfigKind,
  provider: string,
): DataEnumerationsPosSystem[] => {
  if (kind === 'loyalty') {
    if (
      provider === DataEnumerationsLoyaltyProgramProvider.YinzCam ||
      provider === DataEnumerationsLoyaltyProgramProvider.Shopify
    ) {
      return [DataEnumerationsPosSystem.Square, DataEnumerationsPosSystem.Stripe];
    }
    return [];
  }
  if (provider === DataEnumerationsDiscountCodeProvider.Local) {
    return [DataEnumerationsPosSystem.Square];
  }
  if (provider === DataEnumerationsDiscountCodeProvider.Remote) return REAL_POS_SYSTEMS;
  return [];
};

export const enabled = (mode: string): boolean =>
  mode !== DataEnumerationsLookupMode.Disabled;

export const LOOKUP_FIELD_MAP = {
  loyalty: {
    mode: 'loyaltyProgramMode',
    provider: 'loyaltyProgramProvider',
    posSystem: 'loyaltyProgramPosSystem',
    maximumAttempts: 'loyaltyProgramMaximumAttempts',
    activeFrom: 'loyaltyProgramActiveFrom',
    activeTo: 'loyaltyProgramActiveTo',
    buttonLabel: 'loyaltyProgramButtonLabel',
    buttonColor: 'loyaltyProgramButtonColor',
    successMessage: 'loyaltyProgramSuccessMessage',
    failureMessage: 'loyaltyProgramFailureMessage',
  },
  discount: {
    mode: 'discountCodeMode',
    provider: 'discountCodeProvider',
    posSystem: 'discountCodePosSystem',
    maximumAttempts: 'discountCodeMaximumAttempts',
    activeFrom: 'discountCodeActiveFrom',
    activeTo: 'discountCodeActiveTo',
    buttonLabel: 'discountCodeButtonLabel',
    buttonColor: 'discountCodeButtonColor',
    successMessage: 'discountCodeSuccessMessage',
    failureMessage: 'discountCodeFailureMessage',
  },
} as const;

export const lookupSummary = (kind: LookupConfigKind, values: StoreFormValues): string => {
  const fields = LOOKUP_FIELD_MAP[kind];
  const mode = values[fields.mode];
  if (mode === DataEnumerationsLookupMode.Disabled) {
    return kind === 'loyalty' ? 'No loyalty program' : 'Off';
  }
  const posSystem = values[fields.posSystem];
  return [
    values[fields.provider],
    posSystem && posSystemLabel(posSystem as DataEnumerationsPosSystem),
    lookupModeLabel(mode as DataEnumerationsLookupMode),
  ]
    .filter(Boolean)
    .join(' · ');
};

export const lookupSectionFields = (kind: LookupConfigKind): (keyof StoreFormValues)[] =>
  [
    ...Object.values(LOOKUP_FIELD_MAP[kind]),
    ...(kind === 'loyalty'
      ? ['loyaltyProgramHostname', 'loyaltyProgramUsername', 'loyaltyProgramPassword']
      : []),
  ] as (keyof StoreFormValues)[];

export const formatActiveWindow = (
  activeFrom: string | null | undefined,
  activeTo: string | null | undefined,
  zone: string,
): string => {
  const abbreviation = zoneAbbreviation(zone, activeFrom ?? activeTo ?? undefined);
  if (activeFrom && activeTo) {
    return `${formatInstant(activeFrom, zone)} – ${formatInstant(activeTo, zone)} (${abbreviation})`;
  }
  if (activeFrom) return `From ${formatInstant(activeFrom, zone)} (${abbreviation})`;
  if (activeTo) return `Until ${formatInstant(activeTo, zone)} (${abbreviation})`;
  return 'Always active';
};
